import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';
import { useAnalyticsApi } from '@/lib/useAnalytics';

/**
 * PEŁNE DANE OSÓB w zastosowaniach skórek (OWNER 17.10): adresat USER pokazywany jako „imię i nazwisko + pełny
 * e-mail”, nie „Jan K. — szkoła”. Źródło: Administracja → Użytkownicy (ta sama lista co ekran Użytkowników, fraza =
 * identyfikator konta w TREŚCI POST, zdolność `users.read`). Bez tej zdolności zostaje etykieta z systemu głównego.
 */
export interface OsobaPelna { imie: string | null; email: string | null }

/**
 * Pamięć karty przeglądarki: panel zastosowań otwierany z biblioteki nie pyta drugi raz o te same osoby i od razu
 * pokazuje pełne dane (bez mignięcia etykiety „Jan K. — szkoła”). Tylko udane odczyty; przeładowanie strony czyści.
 */
const PAMIEC = new Map<string, OsobaPelna>();

function zPamieci(lista: readonly string[]): Map<string, OsobaPelna> {
  const m = new Map<string, OsobaPelna>();
  lista.forEach((id) => { const o = PAMIEC.get(id); if (o) m.set(id, o); });
  return m;
}

export function useOsobyPelne(ids: readonly string[]): Map<string, OsobaPelna> {
  const auth = useAuth();
  const api = useAnalyticsApi();
  const wolno = auth.has('users.read');
  const klucz = [...new Set(ids.map((id) => id.toLowerCase()))].sort().join(',');
  // Wersja pamięci: odczyt dopisuje osoby do PAMIEC, a podbicie wersji przelicza mapę dla tego widoku.
  const [wersja, setWersja] = useState(0);

  useEffect(() => {
    if (!wolno || !klucz) return;
    let aktualne = true;
    const lista = klucz.split(',');
    const brakujace = lista.filter((id) => !PAMIEC.has(id));
    if (!brakujace.length) return;
    void Promise.all(brakujace.map((id) => api.uzytkownicy({ q: id, rozmiar: 10 })
      .then((o) => o.konta.find((k) => k.id.toLowerCase() === id) ?? null)
      .catch(() => null)))
      .then((konta) => {
        konta.forEach((k, i) => { if (k) PAMIEC.set(brakujace[i]!, { imie: k.imie, email: k.email }); });
        if (aktualne) setWersja((w) => w + 1);
      });
    return () => { aktualne = false; };
  }, [api, wolno, klucz]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- `wersja` to celowy wyzwalacz przeliczenia z PAMIEC
  return useMemo(() => (wolno && klucz ? zPamieci(klucz.split(',')) : new Map<string, OsobaPelna>()), [wolno, klucz, wersja]);
}

/** „Radosław Kowalczyk · radoslaw@…” — pełne dane, a bez nich etykieta systemu głównego. */
export function etykietaOsoby(id: string, zapasowa: string, osoby: Map<string, OsobaPelna>): string {
  const o = osoby.get(id.toLowerCase());
  if (!o) return zapasowa;
  const imie = (o.imie ?? '').trim() || 'brak imienia i nazwiska';
  return o.email ? `${imie} · ${o.email}` : imie;
}
